/**
 * DealBulkEditBar - Barra de edição em massa de deals
 * Exibida no topo quando há deals selecionados
 * 
 * Funcionalidades:
 * - Seletor de Pipeline (bloqueado se deals multi-workspace)
 * - Seletor de Fase (dependente do pipeline)
 * - Seletor de Origem Lead
 * - Multi-select de Tags (adicionar/remover)
 * - Validação cross-workspace no frontend
 * 
 * Versão: 1.0.0
 * RELEASE: 2026-04-09
 */
import React, { useState, useMemo, useEffect } from 'react';
import { X, Save, Layers, CircleDot, Tag, MapPin, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useWorkspacePipelines, WorkspacePipeline } from '@/hooks/useWorkspacePipelines';
import { useDealFilterOptions } from '@/hooks/useDealFilterOptions';
import { useDealTags, DealTag } from '@/hooks/useDealTags';
import { useBulkEditDeals } from '@/hooks/useBulkEditDeals';
import { useAuth } from '@/hooks/useAuth';
import { PaginatedDeal } from '@/hooks/useCrmDealsPaginated';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

// Logger do módulo
const logger = createLogger('DealBulkEditBar');

// ============================================
// TIPOS
// ============================================

interface DealBulkEditBarProps {
  /** Deals selecionados (para extrair workspace) */
  selectedDeals: PaginatedDeal[];
  /** Callback para cancelar/sair do modo edição */
  onCancel: () => void;
  /** Callback após salvar com sucesso */
  onSuccess: () => void;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export function DealBulkEditBar({
  selectedDeals,
  onCancel,
  onSuccess,
}: DealBulkEditBarProps) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Estados de edição
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [selectedPhaseId, setSelectedPhaseId] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [tagsToAdd, setTagsToAdd] = useState<string[]>([]);
  const [tagsToRemove, setTagsToRemove] = useState<string[]>([]);

  // Mutation de edição em massa
  const bulkEdit = useBulkEditDeals();

  // Opções de filtro (sources)
  const { sources } = useDealFilterOptions();

  // Tags do tenant
  const { data: allTags = [] } = useDealTags(tenantId);

  // ============================================
  // DETECÇÃO DE WORKSPACE COMUM
  // ============================================

  /** 
   * Detecta se todos os deals selecionados pertencem ao mesmo workspace
   * Retorna o workspaceId comum ou null se multi-workspace
   */
  const workspaceInfo = useMemo(() => {
    if (selectedDeals.length === 0) return { commonWorkspaceId: null, isMultiWorkspace: false };

    // Agrupa por pipeline_id e depois verifica workspace
    // Como PaginatedDeal não tem workspace_id, precisamos inferir via pipeline
    // Usamos um Map de pipeline_id para agrupar
    const pipelineIds = new Set(selectedDeals.map(d => d.pipeline_id));
    
    // Se todos têm o mesmo pipeline, é o mesmo workspace
    if (pipelineIds.size === 1) {
      return { commonWorkspaceId: 'same-pipeline', isMultiWorkspace: false, pipelineIds };
    }

    // Múltiplos pipelines - precisamos verificar workspace via dados carregados
    return { commonWorkspaceId: null, isMultiWorkspace: true, pipelineIds };
  }, [selectedDeals]);

  // Busca pipelines do workspace (precisa do ID do workspace)
  // Como PaginatedDeal não inclui workspace_id, vamos buscar via o pipeline atual
  // e usar o hook de workspaces para validar
  const { data: workspacePipelines = [] } = useWorkspacePipelines(
    // Usa workspace_id do primeiro deal se disponível
    // Se multi-workspace, desabilita
    workspaceInfo.isMultiWorkspace ? null : 'detect',
    { enabled: false } // Desabilitamos por enquanto, usamos abordagem alternativa
  );

  // ============================================
  // DETECÇÃO DE WORKSPACE VIA PIPELINES CARREGADOS
  // ============================================

  /**
   * Como PaginatedDeal não inclui workspace_id, precisamos de uma abordagem:
   * Usamos o fato de que se todos os deals têm o mesmo pipeline_id,
   * pertencem ao mesmo workspace. Se pipelines diferentes, precisamos
   * consultar o workspace de cada pipeline.
   * 
   * Abordagem simplificada: desabilitar pipeline selector se deals
   * têm pipelines diferentes (conservador mas seguro)
   */
  const canEditPipeline = !workspaceInfo.isMultiWorkspace;

  // Fases do pipeline selecionado ou do pipeline comum
  const selectedPipelinePhases = useMemo(() => {
    // Se selecionou um pipeline novo, busca fases dele
    // Como não temos hook específico para fases de um pipeline arbitrário,
    // e useWorkspacePipelines retorna pipelines com fases,
    // precisamos de outra abordagem.
    // Solução: não mostrar fases até que o pipeline seja selecionado
    return [];
  }, [selectedPipelineId]);

  // ============================================
  // PIPELINE + FASES VIA WORKSPACE PIPELINES
  // ============================================

  // Para buscar pipelines com fases, usamos o workspace do primeiro deal
  // Precisamos do workspace_id, mas PaginatedDeal não tem
  // Vamos adicionar um estado para armazenar o workspace detectado

  const [detectedWorkspaceId, setDetectedWorkspaceId] = useState<string | null>(null);

  // Detecta workspace_id consultando o pipeline do primeiro deal selecionado
  useEffect(() => {
    if (selectedDeals.length === 0 || workspaceInfo.isMultiWorkspace) {
      setDetectedWorkspaceId(null);
      return;
    }

    // Busca workspace do primeiro pipeline
    const fetchWorkspace = async () => {
      const { supabase } = await import('@/integrations/supabase/client');
      const firstPipelineId = selectedDeals[0].pipeline_id;
      
      const { data } = await supabase
        .from('crm_pipelines')
        .select('workspace_id')
        .eq('id', firstPipelineId)
        .single();

      if (data?.workspace_id) {
        logger.debug('Workspace detectado:', data.workspace_id);
        setDetectedWorkspaceId(data.workspace_id);
      }
    };

    fetchWorkspace();
  }, [selectedDeals, workspaceInfo.isMultiWorkspace]);

  // Busca pipelines do workspace detectado
  const { data: pipelinesWithPhases = [], isLoading: pipelinesLoading } = useWorkspacePipelines(
    detectedWorkspaceId,
    { enabled: !!detectedWorkspaceId && canEditPipeline }
  );

  // Fases disponíveis baseadas no pipeline selecionado
  const availablePhases = useMemo(() => {
    if (!selectedPipelineId) return [];
    const pipeline = pipelinesWithPhases.find(p => p.id === selectedPipelineId);
    return pipeline?.phases.filter(ph => !ph.is_won && !ph.is_lost) || [];
  }, [selectedPipelineId, pipelinesWithPhases]);

  // Reset fase quando pipeline mudar
  useEffect(() => {
    setSelectedPhaseId('');
  }, [selectedPipelineId]);

  // ============================================
  // HANDLER DE SUBMIT
  // ============================================

  const handleApply = () => {
    const dealIds = selectedDeals.map(d => d.id);

    // Verifica se há algo para alterar
    const hasChanges = selectedPipelineId || selectedPhaseId || selectedSource || 
                       tagsToAdd.length > 0 || tagsToRemove.length > 0;

    if (!hasChanges) {
      return;
    }

    bulkEdit.mutate(
      {
        dealIds,
        pipelineId: selectedPipelineId || undefined,
        phaseId: selectedPhaseId || undefined,
        source: selectedSource || undefined,
        tagsToAdd: tagsToAdd.length > 0 ? tagsToAdd : undefined,
        tagsToRemove: tagsToRemove.length > 0 ? tagsToRemove : undefined,
      },
      {
        onSuccess: () => {
          onSuccess();
        },
      }
    );
  };

  // Verifica se há alterações pendentes
  const hasChanges = !!(selectedPipelineId || selectedPhaseId || selectedSource || 
                        tagsToAdd.length > 0 || tagsToRemove.length > 0);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-primary/5 border-b border-primary/20 animate-in slide-in-from-top-2 duration-200">
      {/* Contador de seleção */}
      <Badge variant="secondary" className="text-xs font-medium">
        {selectedDeals.length} negócio(s) selecionado(s)
      </Badge>

      <div className="h-4 w-px bg-border" />

      {/* Seletor de Pipeline */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Select
                value={selectedPipelineId}
                onValueChange={setSelectedPipelineId}
                disabled={!canEditPipeline || pipelinesLoading}
              >
                <SelectTrigger className={cn(
                  "w-[150px] h-7 text-xs",
                  !canEditPipeline && "opacity-50 cursor-not-allowed"
                )}>
                  <div className="flex items-center gap-1.5">
                    <Layers className="h-3 w-3 text-muted-foreground" />
                    <span className="truncate">
                      {selectedPipelineId 
                        ? pipelinesWithPhases.find(p => p.id === selectedPipelineId)?.name 
                        : 'Pipeline'}
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {pipelinesWithPhases.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-xs">
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TooltipTrigger>
          {!canEditPipeline && (
            <TooltipContent side="bottom" className="max-w-[220px]">
              <div className="flex items-start gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
                <span className="text-xs">
                  Só é permitido alterar pipeline dos negócios que pertencem ao mesmo Workspace
                </span>
              </div>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>

      {/* Seletor de Fase (dependente do pipeline) */}
      <Select
        value={selectedPhaseId}
        onValueChange={setSelectedPhaseId}
        disabled={!selectedPipelineId || availablePhases.length === 0}
      >
        <SelectTrigger className="w-[140px] h-7 text-xs">
          <div className="flex items-center gap-1.5">
            <CircleDot className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">
              {selectedPhaseId
                ? availablePhases.find(p => p.id === selectedPhaseId)?.name
                : 'Fase'}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {availablePhases.map(ph => (
            <SelectItem key={ph.id} value={ph.id} className="text-xs">
              <div className="flex items-center gap-1.5">
                {ph.color && (
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ph.color }} />
                )}
                {ph.name}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Seletor de Origem Lead */}
      <Select
        value={selectedSource}
        onValueChange={setSelectedSource}
      >
        <SelectTrigger className="w-[140px] h-7 text-xs">
          <div className="flex items-center gap-1.5">
            <MapPin className="h-3 w-3 text-muted-foreground" />
            <span className="truncate">
              {selectedSource || 'Origem'}
            </span>
          </div>
        </SelectTrigger>
        <SelectContent>
          {sources.map(s => (
            <SelectItem key={s} value={s} className="text-xs">
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Tags a Adicionar */}
      <TagMultiSelect
        label="+ Tags"
        tags={allTags}
        selectedIds={tagsToAdd}
        onChange={setTagsToAdd}
        excludeIds={tagsToRemove}
        variant="add"
      />

      {/* Tags a Remover */}
      <TagMultiSelect
        label="- Tags"
        tags={allTags}
        selectedIds={tagsToRemove}
        onChange={setTagsToRemove}
        excludeIds={tagsToAdd}
        variant="remove"
      />

      <div className="flex-1" />

      {/* Botões de Ação */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onCancel}
        className="h-7 text-xs"
      >
        <X className="h-3 w-3 mr-1" />
        Cancelar
      </Button>
      <Button
        size="sm"
        onClick={handleApply}
        disabled={!hasChanges || bulkEdit.isPending}
        className="h-7 text-xs"
      >
        <Save className="h-3 w-3 mr-1" />
        {bulkEdit.isPending ? 'Aplicando...' : 'Aplicar Alterações'}
      </Button>
    </div>
  );
}

// ============================================
// COMPONENTE AUXILIAR: TagMultiSelect
// ============================================

interface TagMultiSelectProps {
  label: string;
  tags: DealTag[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  excludeIds: string[];
  variant: 'add' | 'remove';
}

/**
 * Seletor multi-tag via Popover com checkboxes
 */
function TagMultiSelect({ label, tags, selectedIds, onChange, excludeIds, variant }: TagMultiSelectProps) {
  const [open, setOpen] = useState(false);

  // Filtra tags que não estão na lista de exclusão
  const availableTags = tags.filter(t => !excludeIds.includes(t.id));

  const toggleTag = (tagId: string) => {
    if (selectedIds.includes(tagId)) {
      onChange(selectedIds.filter(id => id !== tagId));
    } else {
      onChange([...selectedIds, tagId]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-7 text-xs gap-1",
            selectedIds.length > 0 && (variant === 'add' ? 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400' : 'border-red-500/50 text-red-700 dark:text-red-400')
          )}
        >
          <Tag className="h-3 w-3" />
          {label}
          {selectedIds.length > 0 && (
            <Badge variant="secondary" className="h-4 px-1 text-[10px]">
              {selectedIds.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-1 max-h-48 overflow-auto">
          {availableTags.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">Nenhuma tag disponível</p>
          ) : (
            availableTags.map(tag => (
              <label
                key={tag.id}
                className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 cursor-pointer"
              >
                <Checkbox
                  checked={selectedIds.includes(tag.id)}
                  onCheckedChange={() => toggleTag(tag.id)}
                />
                <span
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="text-xs truncate">{tag.name}</span>
              </label>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
